import { NavLink, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const links = [
  { to: "/", label: "Home" },
  { to: "/charlie", label: "Meet Charlie" },
  { to: "/books", label: "The Book" },
  { to: "/parents", label: "Parents & Educators" },
  { to: "/waitlist", label: "Waitlist" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-[background,box-shadow,border-color] duration-500 ${
        scrolled
          ? "bg-[#FAF8F5]/85 backdrop-blur-md border-b border-black/5"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 md:px-10 h-16 md:h-20">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <span className="w-9 h-9 rounded-full grid place-items-center bg-[#4A7C7B] text-[#FAF8F5] italic-serif text-lg leading-none">L</span>
          <span className="serif text-lg md:text-xl tracking-tight text-espresso">
            The Love Well Project
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `link-draw text-sm tracking-wide ${isActive ? "text-[#4A7C7B]" : "text-espresso/80 hover:text-espresso"}`
              }
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/waitlist"
            data-testid="nav-join-cta"
            className="ml-2 inline-flex items-center rounded-full bg-[#2C241B] text-[#FAF8F5] px-5 py-2.5 text-sm hover:bg-[#4A7C7B] transition-colors"
          >
            Join the waitlist
          </Link>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((s) => !s)}
          className="md:hidden w-10 h-10 grid place-items-center rounded-full border border-black/10"
          aria-label="Menu"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-panel"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="md:hidden bg-[#FAF8F5]/97 backdrop-blur-lg border-b border-black/5"
          >
            <div className="px-6 py-8 flex flex-col gap-5">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  className={({ isActive }) =>
                    `serif text-2xl ${isActive ? "text-[#4A7C7B] italic" : "text-espresso"}`
                  }
                  end={l.to === "/"}
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/waitlist"
                className="mt-4 inline-flex items-center justify-center rounded-full bg-[#2C241B] text-[#FAF8F5] px-6 py-3"
              >
                Join the waitlist
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
